import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * Live-updating free shipping progress bar.
 *
 * Listens for `cart:update` events dispatched by the theme and refreshes the
 * progress fill, remaining-amount text, and locked/unlocked message without
 * requiring a section re-render.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} fill
 * @property {HTMLElement} inProgressMessage
 * @property {HTMLElement} unlockedMessage
 * @property {HTMLElement} [remaining]
 *
 * @extends {Component<Refs>}
 */
class FreeShippingBar extends Component {
  requiredRefs = ["fill", "inProgressMessage", "unlockedMessage"];

  /** @type {Intl.NumberFormat | null} */
  #formatter = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
  }

  /** @returns {number} */
  get #thresholdCents() {
    return parseInt(this.dataset.thresholdCents ?? "0", 10) || 0;
  }

  /** @returns {Intl.NumberFormat} */
  get #moneyFormatter() {
    if (this.#formatter) return this.#formatter;
    const currency = this.dataset.currency || "USD";
    const locale = this.dataset.locale || "en-US";
    this.#formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
    });
    return this.#formatter;
  }

  /**
   * @param {CustomEvent<{ resource?: { total_price?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const totalPrice = event?.detail?.resource?.total_price;
    if (typeof totalPrice === "number") {
      this.#render(totalPrice);
      return;
    }
    // Fallback when the event omits the cart payload.
    this.#fetchCart();
  };

  async #fetchCart() {
    try {
      const response = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      });
      if (!response.ok) return;
      const cart = await response.json();
      if (typeof cart.total_price === "number") this.#render(cart.total_price);
    } catch (_) {
      // Silently ignore — bar simply won't update this tick.
    }
  }

  /**
   * @param {number} totalCents
   */
  #render(totalCents) {
    const threshold = this.#thresholdCents;
    if (threshold <= 0) return;

    const remainingCents = Math.max(0, threshold - totalCents);
    const progress = Math.min(100, Math.max(0, (totalCents / threshold) * 100));
    const unlocked = remainingCents === 0;

    this.refs.fill.style.width = `${progress}%`;
    this.dataset.state = unlocked ? "unlocked" : "in-progress";

    this.refs.inProgressMessage.hidden = unlocked;
    this.refs.unlockedMessage.hidden = !unlocked;

    if (!unlocked && this.refs.remaining) {
      this.refs.remaining.textContent = this.#moneyFormatter.format(
        remainingCents / 100,
      );
    }
  }
}

if (!customElements.get("free-shipping-bar")) {
  customElements.define("free-shipping-bar", FreeShippingBar);
}